import React, { useEffect, useMemo, useState } from 'react';
import clinicalTemplateAdminService from './clinicalTemplateAdminService.js';

const EMPTY_FORM = {
  bodyPartCode: '',
  displayName: '',
  description: '',
  supportsLaterality: false,
  allowsBilateral: false,
  showContralateralSide: false,
  displayOrder: 0,
  active: true,
};

function normalizeTemplateToForm(template) {
  return {
    bodyPartCode: template?.bodyPartCode || '',
    displayName: template?.displayName || '',
    description: template?.description || '',
    supportsLaterality: Boolean(template?.supportsLaterality),
    allowsBilateral: Boolean(template?.allowsBilateral),
    showContralateralSide: Boolean(template?.showContralateralSide),
    displayOrder:
      template?.displayOrder === null || template?.displayOrder === undefined
        ? 0
        : Number(template.displayOrder),
    active: Boolean(template?.active),
  };
}

function formatDateTime(value) {
  if (!value) {
    return 'N/A';
  }

  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return String(value);
  }

  return parsed.toLocaleString();
}

function validateForm(form) {
  const errors = {};
  const code = String(form.bodyPartCode || '').trim();
  const displayName = String(form.displayName || '').trim();
  const description = String(form.description || '').trim();
  const displayOrder = Number(form.displayOrder);

  if (!code) {
    errors.bodyPartCode = 'Body-part code is required.';
  } else if (code.length > 80) {
    errors.bodyPartCode = 'Body-part code cannot exceed 80 characters.';
  } else if (!/^[A-Za-z0-9_-]+$/.test(code)) {
    errors.bodyPartCode =
      'Use only letters, numbers, underscores, and hyphens.';
  }

  if (!displayName) {
    errors.displayName = 'Display name is required.';
  } else if (displayName.length > 120) {
    errors.displayName = 'Display name cannot exceed 120 characters.';
  }

  if (description.length > 500) {
    errors.description = 'Description cannot exceed 500 characters.';
  }

  if (!Number.isInteger(displayOrder) || displayOrder < 0) {
    errors.displayOrder = 'Display order must be a whole number of 0 or greater.';
  }

  return errors;
}

function buildRequest(form) {
  const supportsLaterality = Boolean(form.supportsLaterality);

  return {
    bodyPartCode: String(form.bodyPartCode || '').trim().toUpperCase(),
    displayName: String(form.displayName || '').trim(),
    description: String(form.description || '').trim() || null,
    supportsLaterality,
    allowsBilateral: supportsLaterality && Boolean(form.allowsBilateral),
    showContralateralSide:
      supportsLaterality && Boolean(form.showContralateralSide),
    displayOrder: Number(form.displayOrder),
    active: Boolean(form.active),
  };
}

export default function BodyPartTemplateAdminSection() {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [actionError, setActionError] = useState('');
  const [success, setSuccess] = useState('');
  const [saving, setSaving] = useState(false);
  const [activeActionId, setActiveActionId] = useState(null);
  const [editingTemplate, setEditingTemplate] = useState(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [formErrors, setFormErrors] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');

  const isEditing = Boolean(editingTemplate?.bodyPartTemplateId);

  const sortedTemplates = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();

    return [...templates]
      .filter((template) => {
        if (statusFilter === 'ACTIVE' && !template.active) {
          return false;
        }

        if (statusFilter === 'INACTIVE' && template.active) {
          return false;
        }

        if (!normalizedSearch) {
          return true;
        }

        return [
          template.bodyPartCode,
          template.displayName,
          template.description,
          template.bodyPartTemplateId,
        ]
          .filter((value) => value !== null && value !== undefined)
          .join(' ')
          .toLowerCase()
          .includes(normalizedSearch);
      })
      .sort((first, second) => {
        const firstOrder = Number(first.displayOrder ?? 0);
        const secondOrder = Number(second.displayOrder ?? 0);

        if (firstOrder !== secondOrder) {
          return firstOrder - secondOrder;
        }

        return String(first.displayName || '').localeCompare(
          String(second.displayName || '')
        );
      });
  }, [templates, searchTerm, statusFilter]);

  const activeCount = useMemo(
    () => templates.filter((template) => template.active).length,
    [templates]
  );

  useEffect(() => {
    loadTemplates();
  }, []);

  const showSuccess = (message) => {
    setSuccess(message);
    window.setTimeout(() => setSuccess(''), 3500);
  };

  const loadTemplates = async () => {
    try {
      setLoading(true);
      setLoadError('');

      const data = await clinicalTemplateAdminService.getAllBodyPartTemplates();
      setTemplates(Array.isArray(data) ? data : []);
    } catch (error) {
      setTemplates([]);
      setLoadError(error?.message || 'Body-part templates could not be loaded.');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEditingTemplate(null);
    setForm({ ...EMPTY_FORM });
    setFormErrors({});
    setActionError('');
  };

  const beginEdit = (template) => {
    setEditingTemplate(template);
    setForm(normalizeTemplateToForm(template));
    setFormErrors({});
    setActionError('');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const updateFormField = (field, value) => {
    setForm((current) => {
      const next = {
        ...current,
        [field]: value,
      };

      if (field === 'supportsLaterality' && !value) {
        next.allowsBilateral = false;
        next.showContralateralSide = false;
      }

      return next;
    });

    setFormErrors((current) => {
      if (!current[field]) {
        return current;
      }

      const next = { ...current };
      delete next[field];
      return next;
    });
  };

  const submitForm = async (event) => {
    event.preventDefault();

    const errors = validateForm(form);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      setActionError('Correct the highlighted fields before saving.');
      return;
    }

    try {
      setSaving(true);
      setActionError('');

      const request = buildRequest(form);

      if (isEditing) {
        await clinicalTemplateAdminService.updateBodyPartTemplate(
          editingTemplate.bodyPartTemplateId,
          request
        );
        showSuccess('Body-part template updated.');
      } else {
        await clinicalTemplateAdminService.createBodyPartTemplate(request);
        showSuccess('Body-part template created.');
      }

      resetForm();
      await loadTemplates();
    } catch (error) {
      setActionError(error?.message || 'Body-part template could not be saved.');
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (template) => {
    const nextActive = !template.active;

    if (!nextActive) {
      const confirmed = window.confirm(
        `Deactivate "${template.displayName}"? Existing historical records will keep their reference, but this body part will no longer be selectable for new configuration.`
      );

      if (!confirmed) {
        return;
      }
    }

    try {
      setActiveActionId(template.bodyPartTemplateId);
      setActionError('');

      await clinicalTemplateAdminService.setBodyPartTemplateActive(
        template.bodyPartTemplateId,
        nextActive
      );

      showSuccess(
        `Body-part template ${nextActive ? 'activated' : 'deactivated'}.`
      );

      if (
        editingTemplate?.bodyPartTemplateId === template.bodyPartTemplateId
      ) {
        setForm((current) => ({
          ...current,
          active: nextActive,
        }));
        setEditingTemplate((current) => ({
          ...current,
          active: nextActive,
        }));
      }

      await loadTemplates();
    } catch (error) {
      setActionError(
        error?.message ||
          `Body-part template could not be ${nextActive ? 'activated' : 'deactivated'}.`
      );
    } finally {
      setActiveActionId(null);
    }
  };

  return (
    <div className="body-part-template-admin">
      <section className="clinical-template-editor-panel">
        <div className="clinical-template-editor-heading clinical-template-editor-heading-row">
          <div>
            <span className="clinical-template-eyebrow">BODY PART CONFIGURATION</span>
            <h2>{isEditing ? 'Edit Body Part Template' : 'Create Body Part Template'}</h2>
            <p>
              Body parts are the parent configuration used by ROM, strength, goals,
              special tests, palpation, pain, clinical content, and other note templates.
            </p>
          </div>

          {isEditing && (
            <button
              type="button"
              className="clinical-template-secondary-button"
              onClick={resetForm}
              disabled={saving}
            >
              + New Body Part
            </button>
          )}
        </div>

        {actionError && (
          <div className="clinical-template-message error" role="alert">
            {actionError}
          </div>
        )}

        {success && (
          <div className="clinical-template-message success" role="status">
            {success}
          </div>
        )}

        <form className="body-part-template-form" onSubmit={submitForm}>
          <div className="clinical-template-form-grid">
            <label>
              Body-Part Code
              <input
                type="text"
                value={form.bodyPartCode}
                onChange={(event) =>
                  updateFormField('bodyPartCode', event.target.value)
                }
                maxLength={80}
                disabled={isEditing || saving}
                placeholder="Example: ANKLE"
              />
              {isEditing && (
                <small>
                  Stable internal code. It cannot be changed after creation.
                </small>
              )}
              {formErrors.bodyPartCode && (
                <span className="clinical-template-field-error">
                  {formErrors.bodyPartCode}
                </span>
              )}
            </label>

            <label>
              Display Name
              <input
                type="text"
                value={form.displayName}
                onChange={(event) =>
                  updateFormField('displayName', event.target.value)
                }
                maxLength={120}
                disabled={saving}
                placeholder="Example: Ankle"
              />
              {formErrors.displayName && (
                <span className="clinical-template-field-error">
                  {formErrors.displayName}
                </span>
              )}
            </label>

            <label>
              Display Order
              <input
                type="number"
                min="0"
                step="1"
                value={form.displayOrder}
                onChange={(event) =>
                  updateFormField('displayOrder', event.target.value)
                }
                disabled={saving}
              />
              {formErrors.displayOrder && (
                <span className="clinical-template-field-error">
                  {formErrors.displayOrder}
                </span>
              )}
            </label>
          </div>

          <label className="clinical-template-full-width-field">
            Administrative Description
            <textarea
              rows="3"
              value={form.description}
              onChange={(event) =>
                updateFormField('description', event.target.value)
              }
              maxLength={500}
              disabled={saving}
              placeholder="Optional description for staff configuring clinical templates"
            />
            <small>{form.description.length}/500 characters</small>
            {formErrors.description && (
              <span className="clinical-template-field-error">
                {formErrors.description}
              </span>
            )}
          </label>

          <div className="clinical-template-checkbox-grid">
            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.supportsLaterality}
                onChange={(event) =>
                  updateFormField('supportsLaterality', event.target.checked)
                }
                disabled={saving}
              />
              <span>
                <strong>Supports LEFT / RIGHT laterality</strong>
                <small>
                  Use this when the body part can be documented as left, right, or
                  bilateral.
                </small>
              </span>
            </label>

            <label
              className={`clinical-template-checkbox-card ${
                !form.supportsLaterality ? 'disabled' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={form.allowsBilateral}
                onChange={(event) =>
                  updateFormField('allowsBilateral', event.target.checked)
                }
                disabled={saving || !form.supportsLaterality}
              />
              <span>
                <strong>Allows bilateral treatment</strong>
                <small>
                  Allows both sides of this same body part on one authorization.
                </small>
              </span>
            </label>

            <label
              className={`clinical-template-checkbox-card ${
                !form.supportsLaterality ? 'disabled' : ''
              }`}
            >
              <input
                type="checkbox"
                checked={form.showContralateralSide}
                onChange={(event) =>
                  updateFormField('showContralateralSide', event.target.checked)
                }
                disabled={saving || !form.supportsLaterality}
              />
              <span>
                <strong>Show contralateral side</strong>
                <small>
                  Allows the opposite side to appear for comparison on unilateral
                  documentation.
                </small>
              </span>
            </label>

            <label className="clinical-template-checkbox-card">
              <input
                type="checkbox"
                checked={form.active}
                onChange={(event) => updateFormField('active', event.target.checked)}
                disabled={saving}
              />
              <span>
                <strong>Active</strong>
                <small>
                  Active body parts can be selected for new authorizations and notes.
                </small>
              </span>
            </label>
          </div>

          <div className="clinical-template-form-actions">
            <button type="submit" disabled={saving}>
              {saving
                ? 'Saving...'
                : isEditing
                  ? 'Save Body Part Changes'
                  : 'Create Body Part'}
            </button>

            <button
              type="button"
              className="clinical-template-secondary-button"
              onClick={resetForm}
              disabled={saving}
            >
              {isEditing ? 'Cancel Edit' : 'Clear Form'}
            </button>
          </div>
        </form>
      </section>

      <section className="clinical-template-editor-panel clinical-template-list-panel">
        <div className="clinical-template-editor-heading clinical-template-editor-heading-row">
          <div>
            <span className="clinical-template-eyebrow">CONFIGURED BODY PARTS</span>
            <h2>Body Part Templates</h2>
            <p>
              {templates.length} total template{templates.length === 1 ? '' : 's'};{' '}
              {activeCount} active.
            </p>
          </div>

          <button
            type="button"
            className="clinical-template-secondary-button"
            onClick={loadTemplates}
            disabled={loading}
          >
            {loading ? 'Refreshing...' : 'Refresh'}
          </button>
        </div>

        <div className="clinical-template-list-toolbar">
          <input
            type="search"
            value={searchTerm}
            onChange={(event) => setSearchTerm(event.target.value)}
            placeholder="Search code, name, description, or ID"
          />

          <select
            value={statusFilter}
            onChange={(event) => setStatusFilter(event.target.value)}
          >
            <option value="ALL">All statuses</option>
            <option value="ACTIVE">Active only</option>
            <option value="INACTIVE">Inactive only</option>
          </select>
        </div>

        {loadError && (
          <div className="clinical-template-message error" role="alert">
            {loadError}
          </div>
        )}

        {loading ? (
          <div className="clinical-template-empty-state">Loading body-part templates...</div>
        ) : sortedTemplates.length === 0 ? (
          <div className="clinical-template-empty-state">
            No body-part templates match the current filter.
          </div>
        ) : (
          <div className="clinical-template-table-wrap">
            <table className="clinical-template-table">
              <thead>
                <tr>
                  <th>Order</th>
                  <th>Body Part</th>
                  <th>Laterality</th>
                  <th>Bilateral</th>
                  <th>Contralateral</th>
                  <th>Status</th>
                  <th>Updated</th>
                  <th>Actions</th>
                </tr>
              </thead>

              <tbody>
                {sortedTemplates.map((template) => (
                  <tr key={template.bodyPartTemplateId}>
                    <td>{template.displayOrder}</td>
                    <td>
                      <strong>{template.displayName}</strong>
                      <span className="clinical-template-code">
                        {template.bodyPartCode}
                      </span>
                      <small>
                        ID #{template.bodyPartTemplateId}
                        {template.description ? ` | ${template.description}` : ''}
                      </small>
                    </td>
                    <td>{template.supportsLaterality ? 'Yes' : 'No'}</td>
                    <td>{template.allowsBilateral ? 'Yes' : 'No'}</td>
                    <td>{template.showContralateralSide ? 'Yes' : 'No'}</td>
                    <td>
                      <span
                        className={`clinical-template-status-badge ${
                          template.active ? 'active' : 'inactive'
                        }`}
                      >
                        {template.active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <span>{formatDateTime(template.updatedAt)}</span>
                      <small>
                        Staff #{template.updatedByStaffId ?? 'N/A'}
                      </small>
                    </td>
                    <td>
                      <div className="clinical-template-row-actions">
                        <button
                          type="button"
                          className="clinical-template-secondary-button"
                          onClick={() => beginEdit(template)}
                          disabled={activeActionId === template.bodyPartTemplateId}
                        >
                          Edit
                        </button>

                        <button
                          type="button"
                          className={
                            template.active
                              ? 'clinical-template-danger-button'
                              : 'clinical-template-activate-button'
                          }
                          onClick={() => toggleActive(template)}
                          disabled={activeActionId === template.bodyPartTemplateId}
                        >
                          {activeActionId === template.bodyPartTemplateId
                            ? 'Saving...'
                            : template.active
                              ? 'Deactivate'
                              : 'Activate'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div className="clinical-template-history-note">
          Body-part templates are not deleted. Deactivate a template when it should no
          longer be used for new records so historical authorizations and clinical notes
          can continue referencing it.
        </div>
      </section>
    </div>
  );
}
